import React, { useCallback, useEffect, useMemo, } from 'react';
import { useTranslation, } from 'react-i18next';
import { useNavigate, } from 'react-router-dom';
import { AtlasForm, IForm, IFormOptions, } from 'atlas-form';
import {
  PrepaidCreditEditForm,
  PrepaidCreditEditInitialValues,
  PrepaidCreditComponents,
  PrepaidCreditAdditionalParams,
  PrepaidCreditFormOptions,
  onPrepaidCreditFormSaved,
  IPrepaidCreditFormOptionsProps,
  AlertError,
} from '@Utils';
import { ISharedPrepaidCredit, ISharedPatchPrepaidCredit, PermissionEnum, CrudEnum, } from 'atlas-shared';
import { fetchPrepaidCredit, patchPrepaidCredit, getPrepaidCreditUpdateSchema, } from '@Api';
import { RuleProtected, } from '@Containers';
import { withEdit, IWithEditProps, } from '@Hocs/with.edit';
import { useAbilities, useAuth, useOrganizations, } from '@Hooks';
import { AppSpinner, } from '@Components';
import { useAppDispatch, } from '@Store';

interface IProps extends IWithEditProps<ISharedPrepaidCredit> {}

const PrepaidCreditFormEdit = ({ id, value, setValue, formSchema, schemaLoading, saving, save, title, onSaved, onCancel, onChange, hasAdvanced = false, }: IProps) => {
  const { t, } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const abilities = useAbilities();
  const dispatch = useAppDispatch();
  const organizations = useOrganizations();

  useEffect(() => {
    fetchPrepaidCredit(id).then(prepaid_credit => setValue(prepaid_credit));
  }, [id, setValue, ]);

  const param_props: IPrepaidCreditFormOptionsProps = {
    t,
    is_new: false,
    abilities,
    organizations: organizations.organizations,
  };
  const formLayout: IForm | undefined = useMemo(() => value && PrepaidCreditEditForm(value, param_props, t), [value, organizations, t, abilities, ]);
  const options: IFormOptions | undefined = useMemo(() => value && PrepaidCreditFormOptions(value, param_props, t), [value, organizations, t, abilities, ]);
  
  const onValuesChange = useCallback((changedValue: any, values: ISharedPatchPrepaidCredit, form) => {
    onChange?.(values, changedValue, form);
  }, []);
  const onFinish = useCallback((values: ISharedPatchPrepaidCredit) => value && save(() => patchPrepaidCredit(value.id, values).then(
    res => onSaved ? onSaved(res, navigate) : onPrepaidCreditFormSaved(t, 'edit', res, navigate),
    err => {
      AlertError(t, {
        content: err,
      });
    }
  )), [value, save, navigate, t, ]);
  
  if (schemaLoading)
    return <AppSpinner />;

  if (!value)
    return <>{t('ITEM_NOT_FOUND')}</>;

  if (!formSchema)
    return <>{t('SCHEMA_NOT_FOUND')}</>;
  
  if (!organizations.loaded)
    return <>{t('LOADING_DEPENDENCIES')}</>;

  return (
    <RuleProtected
      isAllowed={() => !value || abilities.can(value.organization_id, CrudEnum.Update, PermissionEnum.PrepaidCredit, value)}
    >
      <AtlasForm<ISharedPatchPrepaidCredit>
        dispatch={dispatch}
        formLayout={formLayout as IForm}
        form={formSchema}
        options={options}
        components={PrepaidCreditComponents}
        additionalParams={PrepaidCreditAdditionalParams(auth, param_props)}
        onFinish={onFinish}
        onCancel={onCancel}
        onValuesChange={onValuesChange}
        initialValues={PrepaidCreditEditInitialValues(value)}
        saving={saving}
        title={title ? t(title) : `${t('EDIT')} ${t('PREPAID_CREDIT')}`}
        entityId={value.id}
        entity={value}
        hasAdvanced={hasAdvanced}
      />
    </RuleProtected>
  );
};

export const PrepaidCreditFormEditView = React.memo(withEdit(PrepaidCreditFormEdit, getPrepaidCreditUpdateSchema));
